import { Box, Typography } from '@mui/material';
import { pxToRem } from '@geneo2-web/shared-ui';
import { DifficultyStats } from '../../PerformanceDrawer/performanceDrawerData';
import { ProgressBar } from '../../ProgressBar';

type DifficultyBreakdownProps = {
  data: DifficultyStats[];
};

const styles = {
  card: {},

  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: pxToRem(14),
  },

  titleRow: {
    display: 'flex',
    alignItems: 'center',
    gap: pxToRem(8),
  },

  icon: {
    fontSize: pxToRem(18),
  },

  title: {
    fontFamily: 'Poppins',
    fontWeight: 700,
    color: '#1e293b',
    fontSize: pxToRem(14),
  },

  list: {
    display: 'flex',
    flexDirection: 'column',
    gap: pxToRem(10),
  },
};

export const DifficultyBreakdownSection = ({
  data,
}: DifficultyBreakdownProps) => {
  if (!data || data.length === 0) return null;

  return (
    <Box sx={styles.card}>
      {/* HEADER */}
      <Box sx={styles.header}>
        <Box sx={styles.titleRow}>
          <Typography sx={styles.icon}>📊</Typography>
          <Typography sx={styles.title}>Difficulty Breakdown</Typography>
        </Box>
      </Box>

      {/* LIST (reuse ConceptItem as-is) */}
      <Box sx={styles.list}>
        {data.map((item, idx) => (
          <DifficultyItem key={idx} data={item} />
        ))}
      </Box>
    </Box>
  );
};

const LEVEL_CONFIG: Record<
  DifficultyStats['level'],
  { label: string; icon: string; color: string; bg: string; barColor: string }
> = {
  easy: {
    label: 'Easy',
    icon: 'E',
    color: '#16a34a',
    bg: '#dcfce7',
    barColor: '#22c55e',
  },
  medium: {
    label: 'Medium',
    icon: 'M',
    color: '#d97706',
    bg: '#fef3c7',
    barColor: '#f59e0b',
  },
  hard: {
    label: 'Hard',
    icon: 'H',
    color: '#dc2626',
    bg: '#fee2e2',
    barColor: '#ef4444',
  },
};

const formatAvgTime = (seconds: number) => {
  const m = Math.floor(seconds / 60);
  const s = Math.round(seconds % 60);
  return m > 0 ? `${m}:${String(s).padStart(2, '0')}` : `${s}s`;
};

const DifficultyItem = ({ data }: { data: DifficultyStats }) => {
  const cfg = LEVEL_CONFIG[data.level];
  const total = data.total || 0;
  const pct = total > 0 ? Math.round((data.correct / total) * 100) : 0;
  const avgTime = total > 0 ? (data.timeTaken ?? 0) / total : 0;

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: pxToRem(14),
        p: pxToRem(14),
        bgcolor: '#f8fafc',
        borderRadius: pxToRem(14),
        border: '1px solid #f1f5f9',
      }}
    >
      <Box
        sx={{
          width: 40,
          height: 40,
          borderRadius: pxToRem(10),
          bgcolor: cfg.bg,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 16,
          fontWeight: 800,
          color: cfg.color,
          flexShrink: 0,
        }}
      >
        {cfg.icon}
      </Box>

      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            mb: pxToRem(4),
          }}
        >
          <Typography
            sx={{ fontSize: pxToRem(13), fontWeight: 700, color: '#1e293b' }}
          >
            {cfg.label}
          </Typography>
          <Typography
            sx={{ fontSize: pxToRem(13), fontWeight: 700, color: cfg.color }}
          >
            {data.correct}/{total}
          </Typography>
        </Box>

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: pxToRem(8),
          }}
        >
          <Typography
            sx={{
              fontSize: pxToRem(11),
              color: '#94a3b8',
              fontWeight: 500,
              flex: 1,
            }}
          >
            ⏱ Avg {formatAvgTime(avgTime)} per question
          </Typography>
          <Box
            width={pxToRem(80)}
            sx={{
              flex: 1,
            }}
          >
            <ProgressBar valuePercent={pct} color={cfg.barColor} height={6} />
          </Box>
        </Box>
      </Box>

      <Typography
        sx={{
          fontSize: pxToRem(12),
          fontWeight: 700,
          color: cfg.color,
          minWidth: pxToRem(32),
          textAlign: 'right',
        }}
      >
        {pct}%
      </Typography>
    </Box>
  );
};
